import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { Check, Loader2, Film, Star, Eye, MessageCircle, Users, Crown } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/features/auth';
import { api, AVATAR_PALETTE, contrastColor } from '@watchparty/shared';
import Avatar from '@/components/ui/Avatar';
const tabs = [
    {
        id: 'watchlog',
        label: 'WatchLog',
        renderLabel: (isActive) => (_jsxs("span", { style: { opacity: isActive ? 1 : 0.4 }, children: [_jsx("span", { style: { color: 'var(--color-text)' }, children: "Watch" }), _jsx("span", { style: { color: 'var(--color-accent)' }, children: "Log" })] })),
    },
    { id: 'profile', label: 'Profile' },
];
// ---------------------------------------------------------------------------
// TabNav
// ---------------------------------------------------------------------------
function TabNav({ activeTab, onChange }) {
    return (_jsx("div", { style: { display: 'flex', borderBottom: '1px solid var(--color-border)', marginBottom: '24px', overflowX: 'auto' }, children: tabs.map((tab) => (_jsx("button", { onClick: () => onChange(tab.id), style: {
                padding: '10px 16px',
                fontFamily: 'var(--font-family-body)',
                fontSize: '0.875rem',
                fontWeight: 700,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                borderBottom: activeTab === tab.id ? '2px solid var(--color-accent)' : '2px solid transparent',
                color: activeTab === tab.id ? 'var(--color-accent)' : 'var(--color-text-muted)',
                marginBottom: '-1px',
                transition: 'color 200ms, background-color 200ms, opacity 200ms',
                whiteSpace: 'nowrap',
            }, children: tab.renderLabel ? tab.renderLabel(activeTab === tab.id) : tab.label }, tab.id))) }));
}
const STAT_ITEMS = [
    { key: 'partiesCreated', label: 'Created', icon: Crown },
    { key: 'partiesJoined', label: 'Joined', icon: Users },
    { key: 'worksWatched', label: 'Watched', icon: Eye },
    { key: 'worksRated', label: 'Rated', icon: Star },
    { key: 'messagesSent', label: 'Messages', icon: MessageCircle },
];
function StatsBar({ stats }) {
    return (_jsx("div", { style: {
            display: 'flex',
            background: 'var(--color-surface)',
            borderRadius: 'var(--radius-md, 4px)',
            border: '1px solid var(--color-border)',
            marginBottom: '24px',
            overflow: 'hidden',
        }, children: STAT_ITEMS.map((item, i) => {
            const Icon = item.icon;
            const value = stats?.[item.key];
            return (_jsxs("div", { style: {
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '14px 4px',
                    borderLeft: i > 0 ? '1px solid var(--color-border)' : 'none',
                    minWidth: 0,
                }, children: [_jsx(Icon, { size: 14, style: { color: 'var(--color-text-muted)', flexShrink: 0 } }), _jsx("span", { style: {
                            fontFamily: 'var(--font-family-body)',
                            fontSize: '1.125rem',
                            fontWeight: 700,
                            color: 'var(--color-accent)',
                            lineHeight: 1,
                            fontVariantNumeric: 'tabular-nums',
                        }, children: value != null ? value.toLocaleString() : '\u2014' }), _jsx("span", { style: {
                            fontFamily: 'var(--font-family-body)',
                            fontSize: '0.625rem',
                            fontWeight: 600,
                            color: 'var(--color-text-muted)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            lineHeight: 1,
                        }, children: item.label })] }, item.key));
        }) }));
}
const STATUS_ALL = 0;
const STATUS_WATCHED = 1;
const STATUS_WATCHING = 2;
const STATUS_WANTS_TO_WATCH = 3;
const STATUS_TABS = [
    { value: STATUS_ALL, label: 'All' },
    { value: STATUS_WATCHED, label: 'Watched' },
    { value: STATUS_WATCHING, label: 'Watching' },
    { value: STATUS_WANTS_TO_WATCH, label: 'Want to Watch' },
];
function workTypeLabel(workType) {
    switch (workType) {
        case 1: return 'Movie';
        case 2: return 'Series';
        case 3: return 'Season';
        case 4: return 'Episode';
        default: return '';
    }
}
function WatchLogTab() {
    const [activeStatus, setActiveStatus] = useState(STATUS_ALL);
    const statusParam = activeStatus === STATUS_ALL ? undefined : activeStatus;
    const { data: stats } = useQuery({
        queryKey: ['me', 'stats'],
        queryFn: () => api.get('/users/me/stats'),
        staleTime: 1000 * 60 * 5,
    });
    const { data, isLoading } = useQuery({
        queryKey: ['me', 'works', statusParam],
        queryFn: () => api.get(`/users/me/works?pageSize=60${statusParam != null ? `&status=${statusParam}` : ''}`),
    });
    const works = data?.items ?? [];
    return (_jsxs("div", { children: [_jsx(StatsBar, { stats: stats }), _jsx("div", { style: { display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '20px' }, children: STATUS_TABS.map((tab) => {
                    const active = activeStatus === tab.value;
                    return (_jsx("button", { onClick: () => setActiveStatus(tab.value), style: {
                            padding: '5px 14px',
                            borderRadius: '999px',
                            border: active ? '1px solid var(--color-text)' : '1px solid var(--color-border)',
                            background: active ? 'var(--color-text)' : 'none',
                            color: active ? 'var(--color-bg)' : 'var(--color-text-muted)',
                            fontFamily: 'var(--font-family-body)',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'all 150ms ease',
                        }, children: tab.label }, tab.value));
                }) }), isLoading && (_jsx("div", { style: { display: 'flex', justifyContent: 'center', padding: '48px 0' }, children: _jsx(Loader2, { size: 24, style: { color: 'var(--color-accent)', animation: 'spin 1s linear infinite' } }) })), !isLoading && works.length === 0 && (_jsxs("div", { style: { textAlign: 'center', padding: '48px 0', color: 'var(--color-text-muted)' }, children: [_jsx(Film, { size: 32, style: { opacity: 0.3, marginBottom: '8px' } }), _jsx("p", { style: { fontFamily: 'var(--font-family-body)', fontSize: '0.875rem', margin: 0 }, children: activeStatus === STATUS_ALL
                            ? "You haven't tracked anything yet."
                            : `Nothing in your ${STATUS_TABS.find((t) => t.value === activeStatus)?.label.toLowerCase()} list.` })] })), !isLoading && works.length > 0 && (_jsx("div", { style: {
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                    gap: '16px',
                }, children: works.map((work) => {
                    const meta = [work.releaseYear, workTypeLabel(work.workType)].filter(Boolean).join(' · ');
                    return (_jsxs("div", { children: [_jsxs("div", { style: {
                                    aspectRatio: '2 / 3',
                                    borderRadius: 'var(--radius-sm, 4px)',
                                    overflow: 'hidden',
                                    backgroundColor: 'var(--color-surface)',
                                    position: 'relative',
                                }, children: [work.posterUrl ? (_jsx("img", { src: work.posterUrl ?? undefined, alt: work.title, style: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' } })) : (_jsx("div", { style: {
                                            width: '100%',
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'var(--color-text-muted)',
                                        }, children: _jsx(Film, { size: 24 }) })), work.myRating != null && (_jsxs("div", { style: {
                                            position: 'absolute',
                                            top: '4px',
                                            right: '4px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '2px',
                                            padding: '2px 6px',
                                            borderRadius: '999px',
                                            backgroundColor: 'rgba(0,0,0,0.75)',
                                            fontSize: '0.625rem',
                                            fontWeight: 700,
                                            color: '#F0F0F0',
                                        }, children: [_jsx(Star, { size: 10, fill: "#F0F0F0" }), work.myRating] }))] }), _jsx("p", { style: {
                                    margin: '4px 0 0',
                                    fontFamily: 'var(--font-family-body)',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    color: 'var(--color-text)',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }, children: work.title }), meta && (_jsx("p", { style: {
                                    margin: '1px 0 0',
                                    fontFamily: 'var(--font-family-body)',
                                    fontSize: '0.625rem',
                                    color: 'var(--color-text-muted)',
                                }, children: meta }))] }, work.workId));
                }) }))] }));
}
// ---------------------------------------------------------------------------
// ProfileTab
// ---------------------------------------------------------------------------
function ProfileTab() {
    const auth = useAuth();
    const [displayName, setDisplayName] = useState(auth.user?.displayName ?? '');
    const [selectedColor, setSelectedColor] = useState(auth.user?.avatarColor ?? '#6B7280');
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [saved, setSaved] = useState(false);
    async function handleSave() {
        if (!displayName.trim())
            return;
        setIsSaving(true);
        setSaveError(null);
        try {
            await api.put('/auth/me', {
                displayName: displayName.trim(),
                avatarColor: selectedColor,
            });
            await auth.refreshUser();
            setSaved(true);
        }
        catch {
            setSaveError('Failed to save. Please try again.');
        }
        finally {
            setIsSaving(false);
        }
    }
    const labelStyle = {
        display: 'block',
        fontFamily: 'var(--font-family-body)',
        fontSize: '0.75rem',
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: 'var(--color-text-muted)',
        marginBottom: '8px',
    };
    return (_jsxs("div", { style: { maxWidth: '480px', paddingTop: '8px' }, children: [_jsx("div", { style: { display: 'flex', justifyContent: 'center', marginBottom: '32px' }, children: _jsx(Avatar, { displayName: displayName || auth.user?.displayName, avatarUrl: auth.user?.avatarUrl, avatarColor: selectedColor, size: 80 }) }), _jsxs("div", { style: { marginBottom: '28px' }, children: [_jsx("label", { style: labelStyle, children: "Display Name" }), _jsx("input", { value: displayName, onChange: e => {
                            setDisplayName(e.target.value);
                            setSaved(false);
                        }, maxLength: 50, style: {
                            width: '100%',
                            padding: '10px 14px',
                            borderRadius: 'var(--radius-md)',
                            border: '1px solid var(--color-border)',
                            background: 'var(--color-surface)',
                            color: 'var(--color-text)',
                            fontFamily: 'var(--font-family-body)',
                            fontSize: '0.9375rem',
                            outline: 'none',
                            boxSizing: 'border-box',
                            transition: 'border-color 80ms ease',
                        }, onFocus: e => e.target.style.borderColor = 'var(--color-border-strong)', onBlur: e => e.target.style.borderColor = 'var(--color-border)' })] }), _jsxs("div", { style: { marginBottom: '32px' }, children: [_jsx("label", { style: labelStyle, children: "Avatar Color" }), _jsx("div", { style: {
                            display: 'flex',
                            gap: '10px',
                            overflowX: 'auto',
                            padding: '4px 0 8px',
                            scrollbarWidth: 'thin',
                            scrollbarColor: 'var(--color-text-muted) transparent',
                        }, children: AVATAR_PALETTE.map(color => (_jsx("button", { onClick: () => {
                                setSelectedColor(color);
                                setSaved(false);
                            }, title: color, style: {
                                width: '36px',
                                height: '36px',
                                borderRadius: '50%',
                                flexShrink: 0,
                                background: color,
                                border: 'none',
                                boxShadow: selectedColor === color
                                    ? '0 0 0 3px var(--color-text)'
                                    : 'none',
                                cursor: 'pointer',
                                padding: 0,
                                outline: 'none',
                                transition: 'box-shadow 80ms ease',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }, children: selectedColor === color && (_jsx(Check, { size: 14, style: { color: contrastColor(color) } })) }, color))) })] }), saveError && (_jsx("p", { style: {
                    fontFamily: 'var(--font-family-body)',
                    fontSize: '0.875rem',
                    color: 'var(--color-danger, #ef4444)',
                    marginBottom: '12px',
                }, children: saveError })), _jsx("button", { onClick: handleSave, disabled: isSaving || !displayName.trim(), style: {
                    width: '100%',
                    padding: '11px',
                    borderRadius: 'var(--radius-md)',
                    border: 'none',
                    background: saved ? 'var(--color-surface)' : 'var(--color-text)',
                    color: saved ? 'var(--color-text-muted)' : 'var(--color-bg)',
                    fontFamily: 'var(--font-family-body)',
                    fontSize: '0.9375rem',
                    fontWeight: 700,
                    cursor: isSaving || !displayName.trim() ? 'not-allowed' : 'pointer',
                    opacity: isSaving || !displayName.trim() ? 0.6 : 1,
                    transition: 'background 150ms ease, color 150ms ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                }, children: isSaving
                    ? _jsxs(_Fragment, { children: [_jsx(Loader2, { size: 16, style: { animation: 'spin 1s linear infinite' } }), " Saving..."] })
                    : saved
                        ? _jsxs(_Fragment, { children: [_jsx(Check, { size: 16 }), " Saved"] })
                        : 'Save Changes' })] }));
}
// ---------------------------------------------------------------------------
// MePage
// ---------------------------------------------------------------------------
export function MePage() {
    const [activeTab, setActiveTab] = useState('watchlog');
    return (_jsxs("div", { style: { maxWidth: '800px', margin: '0 auto', padding: '24px 16px' }, children: [_jsx("h1", { style: {
                    fontFamily: 'var(--font-family-heading)',
                    fontSize: 'clamp(1.25rem, 3vw, 1.75rem)',
                    fontWeight: 700,
                    color: 'var(--color-text)',
                    marginBottom: '24px',
                }, children: "Me" }), _jsx(TabNav, { activeTab: activeTab, onChange: setActiveTab }), activeTab === 'watchlog' && _jsx(WatchLogTab, {}), activeTab === 'profile' && _jsx(ProfileTab, {})] }));
}
